import React, { useEffect, useMemo, useState } from 'react'
import { Job, updateJobInState, updateJobStatus } from 'redux/actions/admin'
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table'
import { Table, TableContainer } from '../commonUI/Table/styles'
import { Pagination } from '../commonUI/Table/Pagination'
import { useDispatch, useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { Avatar, Box, Flex, PseudoBox, Text, Tooltip, useToast } from '@chakra-ui/core'
import { getFrequencyColor } from 'components/NewsFeed/PollTaskForm/utils'
import LocaleText from 'components/LocaleText'
import { ConsoleBadge } from '../commonUI/ConsoleBadge'
import { getFrequencyLabel, getServiceColor, getServiceName, getStatusColor, getStatusName } from './utils'
import { DateToLocaleValue } from 'components/TasksV2/UI/DateToLocaleValue'
import { InputWithIcon } from '../commonUI/InputWithIcon'
import { Search, Settings } from 'lucide-react'
import { JobsDateRangePicker } from './JobsFilters/JobsDateRangePicker'
import { useUserName } from 'hooks/useUserName'
import useAvatar from 'hooks/useAvatar'
import { NoResults } from 'components/CommonComponents/GroupsTree/NoResults'
import { CustomSkeleton } from 'components/Elements'
import { DateRangeState } from './JobsBoardContent'
import useLocaleText from 'components/useLocaleText'
import dayjs from 'dayjs'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import { useJobsModals } from './JobsModalsManagerContext'
import { AppDispatch } from 'config/redux'
import { ControlledCustomSwitch } from 'components/TasksV2/UI/ControlledCustomSwitch'

const createColumns = () => [
  {
    Header: () => {
      const t_name = useLocaleText('name', true)
      return (
        <Text>
          <LocaleText text={t_name} />
        </Text>
      )
    },

    accessor: 'title',
    Cell: ({ row }: { row: { original: Job } }) => {
      const title = row.original.title
      const description = row.original.content.text

      return (
        <Tooltip label={`${title} - ${description}`} placement="bottom" hasArrow aria-label="Job title" zIndex={10000}>
          <Box w="max-content">
            <Text fontSize="sm" fontWeight="bold" isTruncated maxW="250px">
              {title}
            </Text>

            <Text fontSize="xs" isTruncated maxW="250px">
              {description}
            </Text>
          </Box>
        </Tooltip>
      )
    },
  },
  {
    Header: () => {
      const t_frequency = useLocaleText('jobs_frequency', true)
      return (
        <Text>
          <LocaleText text={t_frequency} />
        </Text>
      )
    },
    accessor: 'recurrence_frequency',
    Cell: ({ row }: { row: { original: Job } }) => {
      const frequencyLabel = getFrequencyLabel(row.original.recurrence_frequency)
      const { text, background, border } = getFrequencyColor(row.original.recurrence_frequency)
      return (
        <ConsoleBadge borderColor={border} bg={background} color={text}>
          <LocaleText text={frequencyLabel} />
        </ConsoleBadge>
      )
    },
  },
  {
    Header: () => {
      const t_status = useLocaleText('jobs_status', true)
      return (
        <Text>
          <LocaleText text={t_status} />
        </Text>
      )
    },
    accessor: 'status',
    Cell: ({ row }: { row: { original: Job } }) => {
      const dispatch: AppDispatch = useDispatch()
      const toast = useToast()
      const { requestConfirmAction } = useJobsModals()

      const status = row.original.status

      const job = row.original
      const updatedJob = job
      const isActive = updatedJob.status === 1

      const { text, background, border } = getStatusColor(status)
      const statusName = getStatusName(status)
      const handleStatusChange = async () => {
        try {
          updatedJob.status = updatedJob.status === 1 ? 0 : 1
          dispatch(updateJobInState(updatedJob))
          await dispatch(updateJobStatus(job.job_id, updatedJob.status))
          toast({
            title: 'Job status updated',
            description: 'Job status updated successfully',
            status: 'success',
            duration: 2000,
            position: 'top-right',
          })
        } catch (error) {
          console.log(`updateJobStatus failed due ${error}`)
          toast({
            title: 'Job status update failed',
            description: 'Job status update failed',
            status: 'error',
            duration: 2000,
            position: 'top-right',
          })
        }
      }

      const onToggle = () => {
        requestConfirmAction(handleStatusChange)
      }
      return (
        <Flex align="center" justify="flex-start" style={{ columnGap: '10px' }}>
          <ConsoleBadge borderColor={border} bg={background} color={text}>
            <LocaleText text={statusName} />
          </ConsoleBadge>
          <ControlledCustomSwitch isChecked={isActive} onChange={onToggle} />
        </Flex>
      )
    },
  },
  {
    Header: () => {
      const t_service = useLocaleText('jobs_service', true)
      return (
        <Text>
          <LocaleText text={t_service} />
        </Text>
      )
    },
    accessor: 'action_service',
    Cell: ({ row }: { row: { original: Job } }) => {
      const service = row.original.action_service
      const { text, background, border } = getServiceColor(service)
      const serviceName = getServiceName(service)
      return (
        <ConsoleBadge borderColor={border} bg={background} color={text}>
          <LocaleText text={serviceName} />
        </ConsoleBadge>
      )
    },
  },
  {
    Header: () => {
      const t_created_by = useLocaleText('jobs_created_by', true)
      return (
        <Text>
          <LocaleText text={t_created_by} />
        </Text>
      )
    },
    accessor: 'created_by',
    Cell: ({ row }: { row: { original: Job } }) => {
      const uid = row.original.created_by
      const fullName = useUserName(uid)
      const avatar = useAvatar(uid)
      return (
        <Flex align="center" style={{ columnGap: '10px' }}>
          <Avatar name={fullName} src={avatar} size="md" />
          <Box>
            <Text maxW="200px" isTruncated fontWeight="medium">
              {fullName}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {uid}
            </Text>
          </Box>
        </Flex>
      )
    },
  },
  {
    Header: () => {
      const label = useLocaleText('next_occurrence', true)
      return (
        <Text>
          <LocaleText text={label} />
        </Text>
      )
    },
    accessor: 'next_occurrence',
    Cell: ({ row }: { row: { original: Job } }) => {
      const timestamp = row.original.next_occurrence
      const parsedDate = timestamp ? dayjs.utc(timestamp, 'YYYYMMDDHHmmss').local().toDate() : null

      return <DateToLocaleValue date={parsedDate} placeholder="N/A" fontSize="14px" fontWeight={500} />
    },
  },
  {
    Header: () => (
      <Text>
        <LocaleText text="admin_actions" />
      </Text>
    ),
    accessor: 'actions',
    Cell: ({ row }: { row: { original: Job } }) => {
      const job = row.original
      const { openJobModal } = useJobsModals()

      const onClick = () => {
        openJobModal(job)
      }

      return (
        <NoFocusButton
          as={PseudoBox}
          borderRadius="5px"
          w="max-content"
          cursor="pointer"
          _hover={{ background: 'colors.greyMedium2' }}
          p="5px"
          onClick={onClick}
        >
          <Settings size={18} />
        </NoFocusButton>
      )
    },
  },
]

interface JobsTableProps {
  jobs: Job[]
  status: number
  loading: boolean
  dateRange: DateRangeState
  onDateRangeChange: (newDateRange: DateRangeState) => void
}

const TableSkeleton = () => {
  const isRtl = useSelector(isRtlSelector)

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            {[
              'name',
              'jobs_frequency',
              'jobs_status',
              'jobs_service',
              'jobs_created_by',
              'next_occurrence',
              'ACTIONS',
            ].map((header, index) => (
              <th key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                <Text>
                  <LocaleText text={header} isLocalUse />
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: 6 }).map((_, cellIndex) => (
                <td key={cellIndex} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                  <CustomSkeleton
                    height={cellIndex === 0 ? '40px' : '20px'}
                    width={cellIndex === 0 ? '300px' : cellIndex === 4 ? '80%' : '60%'}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  )
}

export const JobsTable = ({ jobs, loading, dateRange, onDateRangeChange }: JobsTableProps) => {
  const columns = useMemo(() => createColumns(), [])
  const isRtl = useSelector(isRtlSelector)
  const t_search = useLocaleText('search')

  const [searchValue, setSearchValue] = useState('')

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: jobs,
      initialState: {
        pageIndex: 0,
        pageSize: 12,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  useEffect(() => {
    setGlobalFilter(searchValue)
  }, [searchValue, setGlobalFilter])

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        mb={4}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        style={{ gap: '10px' }}
      >
        <JobsDateRangePicker dateRange={dateRange} onDateRangeChange={onDateRangeChange} />
        <Box w="100%" maxW="300px">
          <InputWithIcon
            icon={<Search />}
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            borderRadius="5px"
            isDisabled={loading}
            placeholder={t_search}
          />
        </Box>
      </Flex>

      {loading ? (
        <TableSkeleton />
      ) : page.length > 0 ? (
        <TableContainer style={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup: any, index: number) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column: any, index: number) => (
                    <th {...column.getHeaderProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any, index: number) => {
                prepareRow(row)
                return (
                  <tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell: any, index: number) => (
                      <td {...cell.getCellProps()} key={index} style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <NoResults />
      )}

      <Pagination
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageIndex={pageIndex}
        pageOptions={pageOptions}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </>
  )
}
